import { useState } from "react";
import { Tooltip } from "@mui/material";
import { Download, Plus } from "lucide-react";

import NameCardForm from "@RegistriesForms/NameCardForm";
import ContactsLabelCardPage from "@RegistriesPages/ContactsLabelCardPage";
import ContactsNameCardPage from "@RegistriesPages/ContactsNameCardPage";
import ExportDownloadForm from "@FormForms/ExportDownloadForm";

import { useReduxProject } from "@Hooks/reduxProjectHook/useReduxProject";
import NameCardService from "@Helpers/URLHelpers/WeboutService/NameCardService";

import "./ContactsPage.scss";

const DEFAULT_DOWNLOAD_OPTIONS = [
    {
        id: 1,
        field: "name",
        description: "The full name of the contact.",
        display: "Name",
        customField: false
    },
    {
        id: 2,
        field: "email",
        description: "The contact's email address.",
        display: "Email",
        customField: false
    },
    {
        id: 3,
        field: "areaCode",
        description: "The area or country code associated with the contact's phone number.",
        display: "Area Code",
        customField: false
    },
    {
        id: 4,
        field: "contactNumber",
        description: "The contact's phone number.",
        display: "Phone",
        customField: false
    },
    {
        id: 5,
        field: "address",
        description: "Address of the contact.",
        display: "Address",
        customField: false
    },
    {
        id: 6,
        field: "department",
        description: "The department or team the contact belongs to.",
        display: "Department",
        customField: false
    },
    {
        id: 7,
        field: "designation",
        description: "The contact's job title or professional designation.",
        display: "Designation",
        customField: false
    },
    {
        id: 8,
        field: "pinCode",
        description: "The postal or PIN code associated with the contact's address.",
        display: "PIN Code",
        customField: false
    },
    {
        id: 9,
        field: "state",
        description: "The state or region associated with the contact's address.",
        display: "State",
        customField: false
    },
    {
        id: 10,
        field: "country",
        description: "The country associated with the contact's address.",
        display: "Country",
        customField: false
    },
    {
        id: 11,
        field: "remarks",
        description: "Additional notes or remarks saved for the contact.",
        display: "Remarks",
        customField: false
    }
];

export default function ContactsPage() {
    const [showConfirmDownloadForm, setShowConfirmDownloadForm] = useState(false);
    const [showNameCardForm, setShowNameCardForm] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [refreshKey, setRefreshKey] = useState(0);

    const [selectedLabel, setSelectedLabel] = useState({
        name: "All Contacts",
        id: 0
    });

    const { currentProjectId } = useReduxProject();

    const nameCardService = new NameCardService();

    const className = "contacts-page";

    const toggleNameCardForm = () => {
        setShowNameCardForm((prev) => !prev);
    };

    const toggleConfirmDownloadForm = () => {
        setShowConfirmDownloadForm((prev) => !prev);
    };

    const handleExport = async ({
        selectedOptions,
        splitBy,
        exportType
    }) => {
        const bodyObj = {
            exportType,
            projectId: currentProjectId,
            selectedLabelId: selectedLabel.id,
            splitBy,
            filterRequest: {
                nameCardFilter: {
                    projectIdList: [currentProjectId]
                },
                options: selectedOptions.map((item) => JSON.stringify(item))
            }
        };

        return await nameCardService.exportReport(bodyObj);
    };

    return (
        <div className={className}>

            <ContactsLabelCardPage
                selectedLabel={selectedLabel}
                setSelectedLabel={setSelectedLabel}
            />

            <main className={`${className}__main`}>

                <header className={`${className}__header`}>

                    <div className={`${className}__header-info`}>
                        <div className={`${className}__breadcrumb`}>
                            Contacts
                            <span>/</span>
                            {selectedLabel?.name}
                        </div>

                        <div className={`${className}__title-row`}>
                            <h1>{selectedLabel?.name}</h1>

                            <span className={`${className}__count`}>
                                {totalCount}
                            </span>
                        </div>

                        <p>
                            Manage and organize your contacts
                        </p>
                    </div>

                    <div className={`${className}__actions`}>

                        {selectedLabel.id === 0 && (
                            <Tooltip title="Export contacts" arrow>
                                <button
                                    type="button"
                                    className={`${className}__btn ${className}__btn--secondary`}
                                    onClick={toggleConfirmDownloadForm}
                                >
                                    <Download size={15} />
                                    <span>Export</span>
                                </button>
                            </Tooltip>
                        )}

                        <Tooltip title="Create a new contact" arrow>
                            <button
                                type="button"
                                className={`${className}__btn ${className}__btn--primary`}
                                onClick={toggleNameCardForm}
                            >
                                <Plus size={15} />
                                <span>Create Contact</span>
                            </button>
                        </Tooltip>

                    </div>
                </header>

                <section className={`${className}__workspace`}>
                    <ContactsNameCardPage
                        refreshKey={refreshKey}
                        setTotalCount={setTotalCount}
                        selectedLabel={selectedLabel}
                    />
                </section>

            </main>

            <ExportDownloadForm
                open={showConfirmDownloadForm}
                closePopup={toggleConfirmDownloadForm}
                defaultOptions={DEFAULT_DOWNLOAD_OPTIONS}
                splitLabels={{
                    "": "None",
                    areaCode: "Area Code",
                    state: "State"
                }}
                onExport={handleExport}
            />

            {showNameCardForm && (
                <NameCardForm
                    open={showNameCardForm}
                    closePopup={toggleNameCardForm}
                    labelId={selectedLabel?.id}
                    reloadData={() =>
                        setRefreshKey((prev) => prev + 1)
                    }
                />
            )}

        </div>
    );
}
